import { auth } from "@/lib/firebase";
import { trpc } from "@/lib/trpc";
import { useQueryClient } from "@tanstack/react-query";
import {
  onAuthStateChanged,
  signOut,
  type User as FirebaseUser,
} from "firebase/auth";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getAuthStartupGateState,
  scheduleAuthStartupTimeout,
} from "@/lib/authStartup";

const isDev = import.meta.env.DEV;

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
};

export function useAuth(options?: UseAuthOptions) {
  const { redirectOnUnauthenticated = false } = options ?? {};
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [firebaseError, setFirebaseError] = useState<unknown>(null);
  const [startupTimedOut, setStartupTimedOut] = useState(false);

  // Listen to Firebase auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      user => {
        setFirebaseUser(user);
        setFirebaseError(null);
        setFirebaseReady(true);
      },
      error => {
        setFirebaseError(error);
        setFirebaseReady(true);
      }
    );
    return unsubscribe;
  }, []);

  // After Firebase auth is resolved, fetch our DB user (with role, etc.)
  // In dev mode, always query — server returns dev user even without token
  /*
    Повторяем запрос при обрыве связи, но НЕ при отказе в правах.

    Сервер честно разделяет два случая (server/_core/context.ts): нет записи в
    базе — это отказ, а недоступная база или перезапуск приложения — ошибка
    SERVICE_UNAVAILABLE, которую он сам называет временной. Клиент же не
    повторял вообще ничего: один неотвеченный запрос — и человек видел «Нет
    доступа. Попросите администратора отправить вам ссылку-приглашение».

    19.09.2026 на это напоролся рекрутёр во время выкатки: приложение
    перезапускалось меньше минуты, а он решил, что его отключили от системы.
    Три попытки с нарастающей паузой перекрывают перезапуск.
  */
  const meQuery = trpc.auth.me.useQuery(undefined, {
    enabled: !!firebaseUser || (isDev && firebaseReady),
    retry: (failureCount, error) => {
      const code = error?.data?.code;
      if (code === "UNAUTHORIZED" || code === "FORBIDDEN") return false;
      return failureCount < 3;
    },
    retryDelay: attempt => Math.min(1000 * 2 ** attempt, 8000),
    refetchOnWindowFocus: false,
  });

  const meQueryEnabled = !!firebaseUser || (isDev && firebaseReady);
  const isInitialLoading =
    !firebaseReady || (meQueryEnabled && meQuery.isPending);
  const hasAuthenticatedUser = !!meQuery.data && (!!firebaseUser || isDev);
  const authErrorCode = meQuery.error?.data?.code;
  const hasTransientError =
    !!firebaseError ||
    (!!meQuery.error &&
      authErrorCode !== "UNAUTHORIZED" &&
      authErrorCode !== "FORBIDDEN");

  useEffect(() => {
    if (!isInitialLoading) {
      setStartupTimedOut(false);
      return;
    }

    setStartupTimedOut(false);
    const timeout = scheduleAuthStartupTimeout(
      () => setStartupTimedOut(true),
      Date.now()
    );
    return () => clearTimeout(timeout);
  }, [isInitialLoading]);

  const { loading: authLoading, showConnectionError: connectionError } =
    getAuthStartupGateState({
      isInitialLoading,
      hasUser: hasAuthenticatedUser,
      timedOut: startupTimedOut,
      hasTransientError,
    });

  const queryClient = useQueryClient();

  const logout = useCallback(async () => {
    queryClient.clear();
    await signOut(auth);
  }, [queryClient]);

  const state = useMemo(
    () => ({
      user: firebaseUser ? (meQuery.data ?? null) : null,
      firebaseUser,
      loading: authLoading,
      connectionError,
      error: meQuery.error ?? null,
      isAuthenticated: hasAuthenticatedUser,
    }),
    [
      authLoading,
      connectionError,
      firebaseUser,
      hasAuthenticatedUser,
      meQuery.data,
      meQuery.error,
    ]
  );

  // Redirect if unauthenticated
  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (!firebaseReady) return;
    if (firebaseUser) return;
    // Don't redirect — just show login screen in DashboardLayout
  }, [redirectOnUnauthenticated, firebaseReady, firebaseUser]);

  return {
    ...state,
    refresh: () => meQuery.refetch(),
    logout,
  };
}
